import { FileUploader, TextArea } from "@caprikorn/dynamo-ui";
import type { FC } from "react";
import { Controller } from "react-hook-form";

import { STANDARD_REQUESTS } from "@services/apiEcms/apiEcms.constants";
import {
  INVALID_EXTENSIONS,
  calculateFileListSize,
  hasInvalidExtensions,
} from "@utils/file.utils";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./RemarkForm.module.css";
import type { RemarkFormProps } from "./RemarkForm.types";

const MAX_ATTACHMENT_SIZE_IN_MO = 10;

export const RemarkForm: FC<RemarkFormProps> = ({ control, actionType }) => {
  const { t } = useCustomTranslation(["main", "common"]);
  const isCommentRequired = STANDARD_REQUESTS.some(
    (request) => request === actionType,
  );

  return (
    <div className={classes.container} data-testid="RemarkForm-RMFormWrapper">
      <Controller
        control={control}
        name="remarkForm.comment"
        render={({ field, fieldState }) => (
          <TextArea
            data-testid="test-requestModal-comment"
            hasError={Boolean(fieldState.error)}
            hint={
              fieldState.error?.message &&
              t(fieldState.error.message, { ns: "common" })
            }
            label={t("requestDrawer.steps.remark.comment")}
            onBlur={field.onBlur}
            onChange={field.onChange}
            placeholder={t("requestDrawer.steps.remark.commentPlaceholder")}
            requirementLabel={
              isCommentRequired
                ? ""
                : t("form.requirement.optional", { ns: "common" })
            }
            value={field.value || ""}
          />
        )}
        rules={{
          required: {
            value: isCommentRequired,
            message: "form.error.required",
          },
        }}
      />
      <Controller
        control={control}
        name="remarkForm.attachments"
        render={({ field, fieldState }) => (
          <FileUploader onChange={field.onChange} value={field.value || []}>
            <FileUploader.Input
              data-testid="test-requestModal-uploader"
              description={t(
                "requestDrawer.steps.remark.attachmentDescription",
              )}
              hasError={Boolean(fieldState.error)}
              hint={
                fieldState.error?.message &&
                t(fieldState.error.message, {
                  ns: "common",
                  invalidExtensions: INVALID_EXTENSIONS,
                  maxSize: MAX_ATTACHMENT_SIZE_IN_MO,
                })
              }
              isMultiple
              label={t("requestDrawer.steps.remark.attachment")}
              onBlur={field.onBlur}
              requirementLabel={t("form.requirement.optional", {
                ns: "common",
              })}
            />
            <FileUploader.Files />
          </FileUploader>
        )}
        rules={{
          validate: {
            maxSizeUpload: (files) => {
              if (!files) return true;

              const filesSize = calculateFileListSize(files);
              if (filesSize > MAX_ATTACHMENT_SIZE_IN_MO) {
                return "form.error.attachmentErrorTooBig";
              }

              return true;
            },
            invalidFileExtension: (files) => {
              if (!files) return true;

              const isInvalidExtension = hasInvalidExtensions(files);
              if (isInvalidExtension) {
                return "form.error.attachmentErrorInvalidExtension";
              }

              return true;
            },
          },
        }}
      />
    </div>
  );
};
